import { Component } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { LANGUAGE_STORAGE_KEY } from '../config/languages';
import { getDefaultLanguage } from '../config/tenants';
import { getFromStorage } from '../utils/storage';

/**
 * Localized fallback strings for the error UI.
 *
 * Self-contained on purpose: ErrorBoundary sits ABOVE LanguageProvider in the
 * React tree (see main.jsx), so it cannot consume the useLanguage() hook, and it
 * must keep working even when the crash happens during provider initialization.
 * The global translations.js map only covers cs/en today, so sk/de are provided
 * here directly rather than relying on a fallback to English.
 */
export const ERROR_MESSAGES = {
  cs: {
    title: 'Něco se pokazilo',
    message: 'Došlo k neočekávané chybě. Zkuste prosím obnovit stránku.',
    refresh: 'Obnovit stránku',
  },
  en: {
    title: 'Something went wrong',
    message: 'We encountered an unexpected error. Please try refreshing the page.',
    refresh: 'Refresh Page',
  },
  sk: {
    title: 'Niečo sa pokazilo',
    message: 'Vyskytla sa neočakávaná chyba. Skúste prosím obnoviť stránku.',
    refresh: 'Obnoviť stránku',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    message: 'Ein unerwarteter Fehler ist aufgetreten. Bitte aktualisieren Sie die Seite.',
    refresh: 'Seite aktualisieren',
  },
};

const SUPPORTED_ERROR_LANGUAGES = Object.keys(ERROR_MESSAGES);

/**
 * Detect the visitor's language without relying on React context.
 *
 * Mirrors the priority used by LanguageProvider (URL prefix → persisted choice →
 * browser preference → tenant default), because ErrorBoundary renders outside
 * that provider. Each step is guarded so a failure (e.g. disabled localStorage)
 * can never itself trigger a crash inside the boundary.
 * @returns {string} A language code present in ERROR_MESSAGES.
 */
export const detectLanguage = () => {
  // 1. Language encoded in the URL path (e.g. /cs/produkty, /sk/kategorie)
  try {
    const firstSegment = window.location.pathname.split('/').filter(Boolean)[0];
    if (firstSegment && SUPPORTED_ERROR_LANGUAGES.includes(firstSegment)) {
      return firstSegment;
    }
  } catch (_) { /* fall through */ }

  // 2. Language previously chosen via the LanguageSelector and persisted by LanguageProvider
  try {
    const stored = getFromStorage(LANGUAGE_STORAGE_KEY);
    if (stored && SUPPORTED_ERROR_LANGUAGES.includes(stored)) {
      return stored;
    }
  } catch (_) { /* fall through */ }

  // 3. Browser language preference
  try {
    const browserLanguages = navigator.languages || [navigator.language];
    for (const lang of browserLanguages) {
      const base = String(lang).toLowerCase().split('-')[0];
      if (SUPPORTED_ERROR_LANGUAGES.includes(base)) {
        return base;
      }
    }
  } catch (_) { /* fall through */ }

  // 4. Tenant default (e.g. domain3.sk → 'sk'), when we have strings for it
  try {
    const tenantDefault = getDefaultLanguage();
    if (SUPPORTED_ERROR_LANGUAGES.includes(tenantDefault)) {
      return tenantDefault;
    }
  } catch (_) { /* fall through */ }

  return 'en';
};

/** Resolve the localized fallback copy for the current visitor. */
export const getErrorMessages = () => ERROR_MESSAGES[detectLanguage()] || ERROR_MESSAGES.en;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    }
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const t = getErrorMessages();
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t.title}</h2>
          <p className="text-gray-600 mb-6">{t.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t.refresh}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
